/* ============================================
   ProFRSH Theme - Main JavaScript
   ============================================ */

window.Profrsh = window.Profrsh || {};

/* --- Utilities --- */
Profrsh.isMobile = () => window.innerWidth < 769;
Profrsh.isTablet = () => window.innerWidth >= 769 && window.innerWidth < 1025;

Profrsh.debounce = (fn, delay = 250) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(null, args), delay);
  };
};

Profrsh.throttle = (fn, limit = 100) => {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      fn.apply(null, args);
      inThrottle = true;
      setTimeout(() => { inThrottle = false; }, limit);
    }
  };
};

Profrsh.formatMoney = (cents, format) => {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const raw = (cents / 100).toFixed(2);
  const value = raw.replace(/\.00$/, '');
  const symbol = window.Shopify?.currency?.active === 'INR' ? '₹'
    : document.body.dataset.currencySymbol || '₹';
  return symbol + value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

Profrsh.trapFocus = (element) => {
  if (!element) return;

  /* Remove previous trap listener if any */
  if (element._trapFocusHandler) {
    element.removeEventListener('keydown', element._trapFocusHandler);
  }

  const focusableElements = element.querySelectorAll(
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
  );
  if (!focusableElements.length) return;
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  element._trapFocusHandler = (e) => {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        lastFocusable?.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        firstFocusable?.focus();
        e.preventDefault();
      }
    }
  };

  element.addEventListener('keydown', element._trapFocusHandler);
  firstFocusable?.focus();
};

Profrsh.pauseBodyScroll = () => {
  document.body.style.overflow = 'hidden';
  document.body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`;
};

Profrsh.resumeBodyScroll = () => {
  document.body.style.overflow = '';
  document.body.style.paddingRight = '';
};

/* --- Count-Up Animation --- */
Profrsh.countUp = (element, target, duration = 2000) => {
  const start = 0;
  const startTime = performance.now();

  const animate = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easeOut = 1 - Math.pow(1 - progress, 3);
    const current = Math.floor(start + (target - start) * easeOut);
    element.textContent = current;
    if (progress < 1) requestAnimationFrame(animate);
    else element.textContent = target;
  };

  requestAnimationFrame(animate);
};

/* --- Quantity Selector --- */
Profrsh.initQuantitySelectors = () => {
  document.querySelectorAll('[data-quantity-selector]').forEach(selector => {
    const input = selector.querySelector('[data-quantity-input]');
    const minusBtn = selector.querySelector('[data-quantity-minus]');
    const plusBtn = selector.querySelector('[data-quantity-plus]');
    if (!input) return;

    const min = parseInt(input.min, 10) || 1;
    const max = parseInt(input.max, 10) || 99;

    if (minusBtn && !minusBtn._qtyBound) {
      minusBtn.addEventListener('click', () => {
        const current = parseInt(input.value, 10) || 1;
        if (current > min) {
          input.value = current - 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      minusBtn._qtyBound = true;
    }

    if (plusBtn && !plusBtn._qtyBound) {
      plusBtn.addEventListener('click', () => {
        const current = parseInt(input.value, 10) || 1;
        if (current < max) {
          input.value = current + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      plusBtn._qtyBound = true;
    }

    if (!input._qtyBound) {
      input.addEventListener('change', () => {
        let val = parseInt(input.value, 10);
        if (isNaN(val) || val < min) val = min;
        if (val > max) val = max;
        input.value = val;
      });
      input._qtyBound = true;
    }
  });
};

/* --- DOM Ready --- */
document.addEventListener('DOMContentLoaded', () => {
  /* Initialize quantity selectors */
  Profrsh.initQuantitySelectors();

  /* Initialize count-up elements */
  const countElements = document.querySelectorAll('[data-count-up]');
  if (countElements.length) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const target = parseInt(entry.target.dataset.countUp, 10);
          Profrsh.countUp(entry.target, target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });
    countElements.forEach(el => observer.observe(el));
  }
});

/* --- Shopify Theme Editor Events --- */
document.addEventListener('shopify:section:load', (event) => {
  /* Re-initialize quantity selectors */
  Profrsh.initQuantitySelectors();

  /* Re-initialize scroll animations for new sections */
  if (window.ScrollAnimator) {
    const newElements = event.target.querySelectorAll('[data-animate]');
    newElements.forEach(el => window.ScrollAnimator.observe(el));
  }
});

document.addEventListener('shopify:section:select', (event) => {
  /* Scroll section into view when selected in editor */
  event.target.scrollIntoView({ behavior: 'smooth', block: 'start' });
});
